import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, message, Modal, Input } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { getInternalReports, approveInternalReport } from '../../services/internalReportingService';

const { TextArea } = Input;

const ApprovalQueueTab = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [reason, setReason] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await getInternalReports({ status: 'Pending Approval' });
      if (res?.status) {
        // Mock filter for items that are actually pending
        setReports(res.data.filter(r => r.status === 'Pending Approval'));
      }
    } catch (err) {
      console.error('Failed to fetch approval queue:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleAction = async () => {
    if (!reason && actionType === 'reject') {
      message.error('Please provide a reason for rejection');
      return;
    }

    try {
      const res = await approveInternalReport(currentReport.id, actionType);
      if (res?.status) {
        message.success(`Report ${actionType}ed successfully`);
        setActionModalVisible(false);
        setReason('');
        fetchReports();
      }
    } catch (err) {
      message.error(`Failed to ${actionType} report`);
    }
  };

  const columns = [
    {
      title: 'Report Name',
      dataIndex: 'reportName',
      key: 'reportName',
    },
    {
      title: 'Report Type',
      dataIndex: 'reportType',
      key: 'reportType',
    },
    {
      title: 'Generated By',
      dataIndex: 'generatedBy',
      key: 'generatedBy',
    },
    {
      title: 'Generation Date',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: () => <Tag color="warning">Pending Approval</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => {
              setCurrentReport(record);
              setActionType('approve');
              setActionModalVisible(true);
            }}
          >
            Approve
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => {
              setCurrentReport(record);
              setActionType('reject');
              setActionModalVisible(true);
            }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="approval-queue-tab">
      <Table
        loading={loading}
        columns={columns}
        dataSource={reports}
        rowKey="id"
        className="reg-table"
      />

      <Modal
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Report`}
        open={actionModalVisible}
        onOk={handleAction}
        onCancel={() => setActionModalVisible(false)}
        okText="Confirm"
        okButtonProps={{ danger: actionType === 'reject' }}
      >
        <div style={{ marginBottom: '16px' }}>
          <strong>Report:</strong> {currentReport?.reportName}
        </div>
        <TextArea
          placeholder={actionType === 'approve' ? 'Add a comment (optional)' : 'Reason for rejection'}
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ApprovalQueueTab;
